import { useState, useEffect, useMemo, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  Flex,
  Typography,
  App,
  Spin,
  Button,
  Pagination,
  Dropdown,
  Upload,
  notification,
  Modal,
} from "antd";

import MainContext from "@/layouts/MainLayout/context";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import ObjectsTable from "@/components/tables/ObjectsTable";
import ExportObjectsModal from "@/components/modals/ExportObjectsModal";
import SubscribeObjectsModal from "@/components/modals/SubscribeObjectsModal";

import PlusIcon from "@/icons/PlusIcon";
import DownloadIcon from "@/icons/DownloadIcon";
import DotsHorizontalIcon from "@/icons/DotsHorizontalIcon";
import SpeakerphoneIcon from "@/icons/SpeakerphoneIcon";

import Filters from "./Filters";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getDefinitionFields } from "@/api/definitionFields";
import {
  getItem as getUnit,
  getEmployeeUnitsOptions,
  getUnitsOptions,
  getUnitsTree,
} from "@/api/units";
import {
  getObjects,
  downloadTemplate,
  importObjects,
  deleteObject,
} from "@/api/objects";
import { getItem as getDefInfo } from "@/api/definitions";
import { subscribe } from "@/api/notices";

import { getPluralizedWord } from "@/utils";
import routes from "@/routes";
import { NUMBER_FIELDS, DATE_FIELDS } from "@/constants/definitions";

const LOCAL_STORAGE_KEY = "objectsTableParams";
const MIN_TABLE_BODY_HEIGHT = 320;

const { Title } = Typography;

const uploadsProps = {
  name: "file",
  action: "/api/objects/import-xlsx/from-import-format",
  multiple: false,
  showUploadList: false,
  accept: ".xlsx",
};

const normalizeSorter = (sorter) => {
  const sortArray = Array.isArray(sorter) ? sorter : [sorter];

  return sortArray
    .filter((item) => item?.order)
    .map((item) => ({
      columnKey: item.columnKey,
      field: item.field,
      order: item.order,
    }));
};

// Страница объектов
// Содержит список объектов с возможностью фильтрации и добавления новых элементов.
// Также есть возможность выбора объектов и выгрузки их (с выбором полей для выгрузки).
const Objects = ({ isAdmins = false }) => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  const { definitionId, unit_uuid } = useParams();
  const { resourcesMenu, setLayoutPending } = useContext(MainContext);
  const navigate = useNavigate();

  // Данные
  const [unitData, setUnitData] = useState(null); // Рабочая группа (если есть)
  const [defInfo, setDefInfo] = useState(); // Информация об описателе
  const [defFields, setDefFields] = useState([]); // поля объекта ( + РГ, из них формируется список фильтров)

  const [units, setUnits] = useState([]); // Рабочие группы
  const [unitsTree, setUnitsTree] = useState([]);
  const [objects, setObjects] = useState([]); // объекты в таблице
  const [isLoading, setIsLoading] = useState(true);
  const [objectsPending, setObjectsPending] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]); // выбранные объекты
  const [selectedRowsMap, setSelectedRowsMap] = useState({});
  const [isDownloadModalShow, setIsDownloadModalShow] = useState(false);

  const initialTableParams = {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
    filters: {
      name: null,
      unit_uuid: unit_uuid ? unit_uuid : null,
      object_type: "all",
      units_uuids: [],
      author_uuid: null,
      created_at: null,
      updated_at: null,
    },
    sort: [
      {
        columnKey: "name",
        field: "name",
        order: "ascend",
      },
    ],
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    defInfo,
    objects,
    tableParams.pagination.pageSize,
  ]);
  const tableBodyHeight = Math.max(
    tableHeight - headerHeight,
    MIN_TABLE_BODY_HEIGHT,
  );

  // Поля для экспорта
  const exportFields = useMemo(() => {
    if (!defInfo || !defInfo.fields) return [];
    return [...(defInfo.fields.self || []), ...(defInfo.fields.template || [])];
  }, [defInfo]);

  const searchFields = (defFields) => {
    if (!defFields) return [];

    return [
      ...defFields
        .filter(({ type }) => type === "text")
        .map(({ field_name }) => field_name),
      "name",
    ];
  };

  const breadcrumbItems = useMemo(() => {
    const items = isAdmins
      ? [
          { title: "Административная панель" },

          {
            title: "Все ресурсы",
          },
        ]
      : [
          {
            title: "Главная",
            href: routes.dashboards,
          },

          unit_uuid && unitData ? { title: unitData.name } : {},
          {
            title: "Ресурсы",
          },
        ];

    const findPath = (menu, id) => {
      let res = [];

      const search = (items, currentItems) => {
        for (const item of items) {
          if (item.child === id) {
            res = currentItems.concat({ title: item.name });
            return true;
          }

          if (
            (item.objects &&
              search(
                item.objects,
                currentItems.concat({ title: item.name }),
              )) ||
            (item.folders &&
              search(item.folders, currentItems.concat({ title: item.name })))
          )
            return true;
        }
        return false;
      };

      search(menu, []);

      return res;
    };

    const resourceItems = findPath(resourcesMenu, definitionId);

    return [...items, ...resourceItems];
  }, [resourcesMenu, definitionId, unit_uuid, unitData, isAdmins]);

  useEffect(() => {
    setSelectedRows([]);
    setSelectedRowsMap({});
    setObjects([]);
    fetchInitialData();

    // eslint-disable-next-line
  }, [definitionId, unit_uuid, isAdmins]);

  //Учет прав при экспорте
  const selectedRowsMeta = useMemo(
    () => selectedRows.map((uuid) => selectedRowsMap[uuid]).filter(Boolean),
    [selectedRows, selectedRowsMap],
  );

  const selectedTotalCount = selectedRows.length;

  const selectedUnavailableCount = useMemo(
    () => selectedRowsMeta.filter((item) => item.readOnly).length,
    [selectedRowsMeta],
  );

  const selectedAvailableRows = useMemo(
    () =>
      selectedRowsMeta
        .filter((item) => !item.readOnly)
        .map((item) => item.uuid),
    [selectedRowsMeta],
  );

  const selectedAvailableCount = selectedAvailableRows.length;
  const hasOnlyUnavailableSelected =
    selectedTotalCount > 0 && selectedAvailableCount === 0;

  const hasPartiallyUnavailableSelected =
    selectedUnavailableCount > 0 && selectedAvailableCount > 0;

  const handleRowsSelect = (selectedRowKeys, selectedRowsData = []) => {
    setSelectedRows(selectedRowKeys);

    setSelectedRowsMap((prev) => {
      const next = { ...prev };

      selectedRowsData.forEach((row) => {
        next[row.uuid] = {
          uuid: row.uuid,
          name: row.name,
          readOnly: row.readOnly,
        };
      });

      Object.keys(next).forEach((uuid) => {
        if (!selectedRowKeys.includes(uuid)) {
          delete next[uuid];
        }
      });

      return next;
    });
  };

  const enrichFieldsWithNspdMeta = (fields = [], sourceFields = []) => {
    const byUuid = new Map(sourceFields.map((field) => [field.uuid, field]));
    const byFieldName = new Map(
      sourceFields.map((field) => [field.field_name, field]),
    );

    return fields.map((field) => {
      const fullField =
        byUuid.get(field.uuid) ||
        byUuid.get(field.field) ||
        byFieldName.get(field.field_name);

      return {
        ...field,
        nspd_key_uuid: fullField?.nspd_key_uuid ?? field.nspd_key_uuid,
        nspd_key: fullField?.nspd_key ?? field.nspd_key,
      };
    });
  };

  // Запрос на сервер для получения полей объекта
  // А так же запрашивается рабочая группа, если она есть, для хлебных крошек
  const fetchInitialData = async () => {
    let isFetch = false;
    setIsLoading(true);

    try {
      // Fixme: в getDefinitionFields получать поля для фильтров или все же в getDefInfo придут?
      // приходят в getDefInfo, но там не все поля, надо проверить и переписать
      const [definition, defFields, { units }, unit, tree] = await Promise.all([
        getDefInfo(definitionId),
        getDefinitionFields(definitionId),
        isAdmins ? getUnitsOptions() : getEmployeeUnitsOptions(),
        unit_uuid && getUnit(unit_uuid),
        unit_uuid
          ? getUnitsTree({ unit_uuid: unit_uuid, from_unit: true })
          : getUnitsTree({ from_unit: true }),
      ]);

      setDefInfo(definition);
      setDefFields(defFields);
      setUnits(units);
      setUnitData(unit || null);
      setUnitsTree(tree);

      // Инициализация параметров из localStorage
      const localParams =
        JSON.parse(
          localStorage.getItem(
            `${user.user_uuid}_${LOCAL_STORAGE_KEY}_${definitionId}_${
              unit_uuid || ""
            }_${isAdmins ? "admins" : ""}`,
          ),
        ) || initialTableParams;

      setTableParams(localParams);

      // Если фильтр по рабочей группе, но ее нет в списке, сбросить фильтр
      if (
        localParams.filters.unit_uuid &&
        !units.some((unit) => unit.uuid === localParams.filters.unit_uuid)
      ) {
        isFetch = true;
        updateFilters(localParams, { unit_uuid: null }, true);
      }

      if (!isFetch) {
        fetchData(localParams, defFields);
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const tableFields = useMemo(() => {
    const sourceFields = [
      ...(defInfo?.fields?.self || []),
      ...(defInfo?.fields?.template || []),
    ];

    return enrichFieldsWithNspdMeta(
      defInfo?.tableForm?.fields || [],
      sourceFields,
    );
  }, [defInfo]);

  // Форматируем значения фильтров для корректной передачи на сервер
  const formatFilters = (fields, filters) => {
    return Object.keys(filters).reduce((acc, val) => {
      const field = fields.find((field) => field.field_name === val);
      const isSystemMomentFilter = ["created_at", "updated_at"].includes(val);

      if (filters[val] === "notFilled" || filters[val] === "filled") {
        acc[val] = filters[val];
        return acc;
      }

      if (
        isSystemMomentFilter ||
        NUMBER_FIELDS.includes(field?.type) ||
        DATE_FIELDS.includes(field?.type)
      ) {
        acc[val] = filters[val] ? JSON.stringify(filters[val]) : null;
      } else {
        acc[val] = filters[val];
      }
      return acc;
    }, {});
  };

  // Запрос на сервер для получения данных
  const fetchData = async (tParams, defFields) => {
    setObjectsPending(true);
    window.__MAP_SCREENSHOT_RENDERER__?.reset();

    const currentFilters = formatFilters(
      defFields,
      tParams ? tParams.filters : tableParams.filters,
    );

    const search = Object.keys(currentFilters).reduce((acc, field_name) => {
      const val = currentFilters[field_name];
      if (searchFields(defFields).includes(field_name) && val) {
        // notFilled должен быть обычным фильтром
        if (val === "notFilled") {
          return acc;
        }
        acc = { ...acc, [field_name]: val };
        delete currentFilters[field_name];
      }
      return acc;
    }, {});

    const sortArray = tParams ? tParams.sort : tableParams.sort;

    const sortString = sortArray
      .map(({ columnKey, order }) => {
        return order === "descend" ? `-${columnKey}` : columnKey;
      })
      .join(",");

    const params = {
      definition_uuid: definitionId,
      page: tParams
        ? tParams.pagination.current
        : tableParams.pagination.current,
      perpage: tParams
        ? tParams.pagination.pageSize
        : tableParams.pagination.pageSize,
      sort: sortString,
      ...currentFilters,
      ...(unit_uuid && { unit_uuid }),
      search: JSON.stringify(search),
      type_filter: currentFilters.object_type,
    };

    // Дерево работает с объектами, поэтому для запроса нужно преобразовать их в массив uuids
    if (params.units_uuids?.length) {
      params.units_uuids = params.units_uuids.map(({ value }) => value);
    }

    try {
      const { total, page, perPage, objects } = await getObjects(
        params,
        isAdmins,
      );

      setObjects(objects);

      const updatedTableParams = {
        ...(tParams || tableParams),
        pagination: {
          ...(tParams ? tParams.pagination : tableParams.pagination),
          total,
          current: page,
          pageSize: perPage,
        },
      };
      // Сохраняем параметры пагинации в localStorage
      setParams(updatedTableParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setObjectsPending(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}_${definitionId}_${
        unit_uuid || ""
      }_${isAdmins ? "admins" : ""}`,
      JSON.stringify(params),
    );
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updatedTableParams = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updatedTableParams);

    fetchData(updatedTableParams, defFields);
  };

  // Обработчик для смены фильтров
  const handleFilterChange = (newFilters) => {
    updateFilters(
      tableParams,
      {
        ...newFilters,
      },
      true,
    );
  };

  // Очистка всех фильтров
  const clearFilters = () => {
    const updatedTableParams = {
      ...tableParams,
      filters: { ...initialTableParams.filters },
      pagination: {
        ...tableParams.pagination,
        current: 1, // Сбрасываем на первую страницу
      },
    };

    setParams(updatedTableParams);

    fetchData(updatedTableParams, defFields);
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    const updatedTableParams = {
      ...tableParams,

      pagination: {
        ...tableParams.pagination,
        current: pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      sort: normalizeSorter(sorter),
    };
    setParams(updatedTableParams);

    fetchData(updatedTableParams, defFields);
  };

  const handlePaginationChange = (current, pageSize) => {
    window.__MAP_SCREENSHOT_RENDERER__?.reset();
    const updatedTableParams = {
      ...tableParams,

      pagination: {
        ...tableParams.pagination,
        current,
        pageSize,
      },
    };
    setParams(updatedTableParams);

    fetchData(updatedTableParams, defFields);
  };

  const handleAddNew = () => {
    const initData = { ...tableParams.filters };

    Object.keys(initData).forEach((key) => {
      const value = initData[key];

      // Удаляем массивы, заполненные/не заполненные и пустые значения, и доступность
      if (
        Array.isArray(value) ||
        ["filled", "notFilled"].includes(value) ||
        value === null ||
        value === undefined ||
        value === "" ||
        key === "object_type"
      ) {
        delete initData[key];
      } else if (
        value &&
        typeof value === "object" &&
        (Object.prototype.hasOwnProperty.call(value, "from") ||
          Object.prototype.hasOwnProperty.call(value, "to"))
      ) {
        // Обрабатываем диапазоны
        const { from, to } = value;
        const nextValue = to ?? from;

        if (nextValue !== undefined && nextValue !== null && nextValue !== "") {
          initData[key] = nextValue;
        } else {
          delete initData[key];
        }
      }
    });

    navigate(
      unit_uuid
        ? routes.unitObjectNew(unit_uuid, definitionId)
        : isAdmins
          ? routes.administration.objectNew(definitionId)
          : routes.objectNew(definitionId),
      {
        state: { copiedObject: initData },
      },
    );
  };

  const handleExport = ({ folder_uuid, uuid, original_name }) => {
    setIsDownloadModalShow(false);

    // if (folder_uuid) navigate(routes.folder(folder_uuid));

    if (uuid) {
      const link = document.createElement("a");
      link.href = `/api/file/download?uuid=${uuid}`;
      link.download = original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: "Экспорт успешно завершен",
        duration: 10,
        showProgress: true,
        pauseOnHover: true,
        style: {
          width: 480,
        },
        description: (
          <>
            {folder_uuid && (
              <p>
                Файл сохранен в личную рабочую группу в папку{" "}
                <a href={routes.folder(folder_uuid)}>Выгрузка</a>.
              </p>
            )}
          </>
        ),
      });
    }
  };

  const beforeUploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("definition_uuid", definitionId);

    try {
      const data = await importObjects(formData, isAdmins);
      fetchData(null, defFields);
      if (data.error) {
        notification.error({
          message: "Ошибка при импорте данных",
          duration: 10,
          showProgress: true,
          pauseOnHover: true,
          style: {
            width: 470,
          },
          description: (
            <>
              <p>{data.error}</p>

              {data.file && (
                <>
                  <p>
                    Всего объектов: {data.statistics.total}. Загружено:{" "}
                    {data.statistics.loaded}. Ошибок: {data.statistics.failed}.
                  </p>
                  <p>
                    Файл сохранен в личную рабочую группу в папку{" "}
                    <a href={routes.folder(data.file.folder_uuid)}>Загрузка</a>.
                  </p>
                  <Button
                    type="primary"
                    href={`/api/file/download?uuid=${data.file.uuid}`}
                    download={data.file.original_name}
                  >
                    Скачать файл
                  </Button>
                </>
              )}
            </>
          ),
        });
      } else {
        notification.success({
          message: "Данные успешно импортированы",
          duration: 10,
          showProgress: true,
          pauseOnHover: true,
          style: {
            width: 470,
          },
          description: (
            <div>
              {data.file && (
                <>
                  <p>
                    Всего объектов: {data.statistics.total}. Загружено:{" "}
                    {data.statistics.loaded}. Ошибок: {data.statistics.failed}.
                  </p>
                  <p>
                    Файл сохранен в личную рабочую группу в папку{" "}
                    <a href={routes.folder(data.file.folder_uuid)}>Загрузка</a>.
                  </p>
                  <Button
                    type="primary"
                    href={`/api/file/download?uuid=${data.file.uuid}`}
                    download={data.file.original_name}
                  >
                    Скачать файл
                  </Button>
                </>
              )}
            </div>
          ),
        });
      }
    } catch (error) {
      message.error(error.message);
    }

    return false;
  };

  const handleDownloadTemplate = async (isTemplate = false) => {
    setLayoutPending(true);
    try {
      const file = await downloadTemplate(
        {
          definition_uuid: definitionId,
          objects: isTemplate ? undefined : selectedAvailableRows,
        },
        isAdmins,
      );

      if (file.uuid) {
        const link = document.createElement("a");
        link.href = `/api/file/download?uuid=${file.uuid}`;
        link.download = file.original_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        notification.success({
          message: isTemplate
            ? "Скачан шаблон данных"
            : "Скачаны объекты для редактирования",
          duration: 10,
          showProgress: true,
          pauseOnHover: true,
          style: {
            width: 470,
          },
          description: (
            <>
              {file.folder_uuid && (
                <p>
                  Файл сохранен в личную рабочую группу в папку{" "}
                  <a href={routes.folder(file.folder_uuid)}>Загрузка</a>.
                </p>
              )}
            </>
          ),
        });
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setLayoutPending(false);
    }
  };

  // Подписка
  const getObjectById = (id) => objects.find((o) => o.uuid === id);

  const selectedOptions = useMemo(
    () =>
      selectedRows
        .map((id) => {
          const o = getObjectById(id);
          return o
            ? { value: o.uuid, label: o.name || "(без названия)" }
            : null;
        })
        .filter(Boolean),
    // eslint-disable-next-line
    [selectedRows, objects],
  );

  const handleSubscribe = async () => {
    if (!selectedRows.length) return;
    setSubscribeLoading(true);

    if (selectedRows.length === 1) {
      // одиночная подписка сразу
      const onlyId = selectedRows[0];
      const name = getObjectById(onlyId)?.name || "Объект";

      await subscribe({
        type: "object",
        user: user.user_uuid,
        definition_uuid: definitionId,
        items: selectedRows,
      });

      showNotice(1, name);
    } else {
      // множественный выбор — открываем модалку
      setIsSubscribeModalOpen(true);
    }
    setSubscribeLoading(false);
  };

  const handleConfirmSubscribeMany = async (ids) => {
    if (!ids.length) {
      setIsSubscribeModalOpen(false);
      return;
    }

    try {
      await subscribe({
        type: "object",
        user: user.user_uuid,
        definition_uuid: definitionId,
        items: ids,
      });

      setIsSubscribeModalOpen(false);
      showNotice(ids.length);
    } catch (e) {
      message.error(e.message || "Не удалось оформить подписку");
    }
  };

  const showNotice = (count = 1, name = "") => {
    const title = `Подписка добавлена: ${
      count === 1 ? name : "Несколько объектов"
    }`;
    const description =
      count > 1
        ? "Вы подписались на несколько объектов раздела. Все события, связанные с этими объектами, будут у вас в уведомлениях."
        : "Вы подписались на объект раздела. Все события, связанные с этим объектом, будут у вас в уведомлениях.";

    const style = {
      border: "1px solid #B7EB8F",
      background: "#f6ffed",
      borderRadius: 8,
      width: 500,
    };

    notification.open({
      message: title,
      description: description,
      icon: null,
      duration: 4,
      pauseOnHover: true,
      placement: "bottomRight",
      style,
    });
  };

  // Обработчик удаления
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${item.name}?`,
      content: "Это действие нельзя будет отменить. Данные будут удалены.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем объект после подтверждения
  const handleConfirmDelete = async (item) => {
    setObjectsPending(true);
    try {
      await deleteObject({ uuid: item.uuid, definition_uuid: definitionId });
      await fetchData(tableParams, defFields);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setObjectsPending(false);
    }
  };

  const handleUpdateObjectLocally = (uuid, value) => {
    setObjects((prev) =>
      prev.map((obj) => (obj.uuid === uuid ? { ...obj, ...value } : obj)),
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        defInfo && (
          <>
            <Flex vertical gap={25} className="h-100">
              <Flex vertical gap={10}>
                <Breadcrumbs items={breadcrumbItems} />

                <Flex gap={20} justify="space-between" align="center">
                  <Flex vertical gap={10}>
                    <Title level={2} className="no-margin">
                      {defInfo.name}
                    </Title>
                    {defInfo.description && <div>{defInfo.description}</div>}
                  </Flex>

                  {(defInfo.type === "object" ||
                    (defInfo.type === "repository" && isAdmins)) && (
                    <Flex align="center" gap={8}>
                      {!!selectedRows.length && (
                        <>
                          <div>{`Выбрано ${
                            selectedRows.length
                          } ${getPluralizedWord(selectedRows.length, [
                            "объект",
                            "объекта",
                            "объектов",
                          ])}`}</div>
                          <Button
                            type="primary"
                            icon={<DownloadIcon />}
                            title={
                              hasOnlyUnavailableSelected
                                ? "Выбранные объекты недоступны для скачивания"
                                : undefined
                            }
                            disabled={
                              !selectedRows.length || hasOnlyUnavailableSelected
                            }
                            onClick={() => setIsDownloadModalShow(true)}
                          >
                            Скачать
                          </Button>
                          <Button
                            title="Подписаться на обновления"
                            variant="text"
                            type="text"
                            icon={<SpeakerphoneIcon />}
                            onClick={handleSubscribe}
                            style={{ padding: 0 }}
                            disabled={subscribeLoading}
                          />
                        </>
                      )}

                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "downloadTemplate",
                              label: "Скачать шаблон данных",
                              onClick: () => handleDownloadTemplate(true),
                            },
                            selectedRows.length && {
                              key: "downloadObjects",
                              label: "Скачать объекты для редактирования",
                              disabled: hasOnlyUnavailableSelected,
                              onClick: () => handleDownloadTemplate(false),
                            },
                            {
                              key: "import",
                              label: (
                                <Upload
                                  {...uploadsProps}
                                  beforeUpload={beforeUploadFile}
                                >
                                  Импорт объектов из Excel
                                </Upload>
                              ),
                            },
                          ],
                        }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <Button
                          color="default"
                          variant="text"
                          type="text"
                          icon={<DotsHorizontalIcon />}
                          style={{ padding: 0 }}
                        />
                      </Dropdown>

                      <Button
                        type="primary"
                        icon={<PlusIcon />}
                        onClick={handleAddNew}
                      ></Button>
                    </Flex>
                  )}
                </Flex>
              </Flex>

              <Flex vertical gap={20} style={{ flex: 1, minHeight: 0 }}>
                <Filters
                  initialTableParams={initialTableParams}
                  filters={tableParams.filters}
                  filtersGroups={defInfo?.filterGroups}
                  unitsTree={unitsTree}
                  units={units}
                  isAdmins={isAdmins}
                  user={user}
                  isRepository={defInfo?.type === "repository"}
                  onChange={handleFilterChange}
                  clearFilters={clearFilters}
                />

                <div
                  ref={tableContainerRef}
                  style={{
                    flex: 1,
                    minHeight: headerHeight + MIN_TABLE_BODY_HEIGHT,
                  }}
                >
                  <ObjectsTable
                    sortedInfo={tableParams.sort}
                    objects={objects}
                    fields={tableFields}
                    loading={objectsPending}
                    pagination={false}
                    onChange={handleTableChange}
                    showUnit={defInfo?.type !== "repository"}
                    isNspdEnabled={!!defInfo?.is_nspd_enabled}
                    unit_uuid={unit_uuid}
                    units={units}
                    definitionId={definitionId}
                    showSelect={defInfo?.type === "object" || isAdmins}
                    selectedRowKeys={selectedRows}
                    onRowsSelect={handleRowsSelect}
                    scroll={{ x: "100%", y: tableBodyHeight }}
                    isAdmins={isAdmins}
                    resize
                    color={defInfo.color}
                    onDeleteItem={handleDeleteItem}
                    onRefresh={() => fetchData(tableParams, defFields)}
                    canBeEdited={defInfo.type === "object"}
                    definitionType={defInfo.type}
                    onLocalUpdate={handleUpdateObjectLocally}
                  />
                </div>

                <div style={{ minHeight: 32 }}>
                  <Pagination
                    {...tableParams.pagination}
                    onChange={handlePaginationChange}
                  />
                </div>
              </Flex>
            </Flex>

            {isDownloadModalShow && (
              <ExportObjectsModal
                objects={selectedAvailableRows}
                fields={exportFields}
                definition={definitionId}
                visible={isDownloadModalShow}
                onSave={handleExport}
                onCancel={() => setIsDownloadModalShow(false)}
                onClearAndCancel={() => {
                  setSelectedRows([]);
                  setSelectedRowsMap({});
                  setIsDownloadModalShow(false);
                }}
                isAdmins={isAdmins}
                exportAccessInfo={{
                  total: selectedTotalCount,
                  available: selectedAvailableCount,
                  unavailable: selectedUnavailableCount,
                  hasWarning: hasPartiallyUnavailableSelected,
                }}
              />
            )}
          </>
        )
      )}

      <SubscribeObjectsModal
        open={isSubscribeModalOpen}
        options={selectedOptions}
        onCancel={() => setIsSubscribeModalOpen(false)}
        onConfirm={handleConfirmSubscribeMany}
      />
    </>
  );
};

export default Objects;
